"use client"

import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material"
import { useLocale, useTranslations } from "next-intl"
import { LegacyRef, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, useForm } from "react-hook-form"
import { generateSchema } from "utils"
import { postCareerForm } from "utils/api"
import * as yup from "yup"
import Loading from "../Common/Loading"

const CustomIcon = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="11.5" fill="white" stroke="#D1D5DB" />
  </svg>
)

const CustomCheckedIcon = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="11.5" stroke="#008761" />
    <circle cx="12" cy="12" r="6" fill="#008761" />
  </svg>
)

declare global {
  interface Window {
    __ctm?: {
      config?: {
        sid?: string
      }
    }
  }
}

const StyledButton = styled(Button)<{ $isDisabled: boolean }>`
  width: 100%;

  border: ${(p) => (p.$isDisabled ? "1px solid #d1d5db" : "none")};
  color: #051711;
  background-color: ${(p) => (p.$isDisabled ? "transparent" : "#008761")};
`

const StyledWrapperForm = styled.div`
  padding-top: 32px;
`

const StyledInputDivs = styled.div`
  padding-top: 24px;
`
const StyledSubtitle = styled(Typography)<{ component?: React.ElementType }>`
  font-size: 30px;
  margin: 0px;
`

const FormContainer = styled.form``

const generatedSchema = generateSchema("he")

type Props = {
  setSubmitForm: any
  setIsFormError: any
}

const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const CareerForm = ({ setSubmitForm, setIsFormError }: Props) => {
  const reCaptchaRef = useRef<ReCAPTCHA>()
  const [pending, setPending] = useState(false)
  const locale = useLocale()
  const t = useTranslations("Forms")
  const schema = yup.object({
    fullName: generatedSchema.fields.fullname,
    phone: generatedSchema.fields.phonehe,
    city: generatedSchema.fields.city,
    ID: generatedSchema.fields.identity,
    email: yup
      .string()
      .email("כתובת האימייל שהוזנה אינה תקינה") // optional email with format validation
      .notRequired(),
    learningArea: yup.string().required(),
  })
  type FormData = yup.InferType<typeof schema>

  const {
    handleSubmit,
    control,
    setValue,
    register,
    formState: { errors, isSubmitted, isValid, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      fullName: "",
      phone: "",
      city: "",
      ID: "",
      email: "",
      learningArea: "חולון",
    },
    resolver: yupResolver(schema),
    mode: "onChange",
  })

  const onSubmit = async (data: FormData) => {
    const gRecaptchaResponse =
      (await reCaptchaRef?.current?.executeAsync()) as string
    reCaptchaRef?.current?.reset()
    setPending(true)
    try {
      const formdata = {
        fullName: data.fullName as string,
        cellular: data.phone as string,
        chooseCollege: data.learningArea as string,
        city: data.city as string,
        id: data.ID as string,
        email: data?.email as string,
        hasLicense: false,
        language: "he",
        sid: window?.__ctm?.config?.sid || "",
        recaptchaResponse: gRecaptchaResponse,
      }
      const response = await postCareerForm(formdata)
      if (response && response.body) {
        setSubmitForm(true)
        setIsFormError(false)
      } else {
        setSubmitForm(true)
        setIsFormError(true)
      }
    } catch (error) {
      setSubmitForm(true)
      setIsFormError(true)
    } finally {
      setPending(false)
    }
  }
  return (
    <>
      {pending ? (
        <Loading />
      ) : (
        <>
          <StyledSubtitle component="h2"> {t("jobApplication")}</StyledSubtitle>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            <StyledWrapperForm>
              <Typography variant="body2">
                {t("mandatoryDetails")}{" "}
                <span style={{ color: "#E00700" }}>*</span>
              </Typography>
              <StyledInputDivs>
                <Controller
                  name="fullName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={
                        <>
                          {t("fullName")}
                          <span style={{ color: "#E00700" }}>*</span>
                        </>
                      }
                      type="text"
                      variant="filled"
                      error={!!errors.fullName}
                      ref={register("fullName").ref}
                      helperText={errors.fullName?.message}
                      sx={{
                        marginBlockEnd: "44px",
                      }}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={
                        <>
                          {t("mobileNumber")}
                          <span style={{ color: "#E00700" }}>*</span>
                        </>
                      }
                      type="text"
                      variant="filled"
                      ref={register("phone").ref}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      sx={{
                        marginBlockEnd: "44px",
                      }}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={
                        <>
                          {t("cityResidence")}
                          <span style={{ color: "#E00700" }}>*</span>
                        </>
                      }
                      type="text"
                      variant="filled"
                      ref={register("city").ref}
                      error={!!errors.city}
                      helperText={errors.city?.message}
                      sx={{
                        marginBlockEnd: "44px",
                      }}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="ID"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={
                        <>
                          {t("idCard")}
                          <span style={{ color: "#E00700" }}>*</span>
                        </>
                      }
                      type="text"
                      variant="filled"
                      ref={register("ID").ref}
                      error={!!errors.ID}
                      helperText={errors.ID?.message}
                      sx={{
                        marginBlockEnd: "44px",
                      }}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("mobileOptional")}
                      type="text"
                      variant="filled"
                      ref={register("email").ref}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      sx={{
                        marginBlockEnd: "44px",
                      }}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="learningArea"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      component="fieldset"
                      error={!!errors.learningArea}
                      sx={{ marginBlockEnd: "44px" }}
                    >
                      <FormLabel
                        component="legend"
                        style={{ fontWeight: 700, fontSize: "18px" }}
                      >
                        {t("studyCity")}
                      </FormLabel>
                      <RadioGroup row {...field} style={{ paddingTop: "16px" }}>
                        <FormControlLabel
                          value="חולון"
                          control={
                            <Radio
                              icon={CustomIcon}
                              checkedIcon={CustomCheckedIcon}
                            />
                          }
                          label={locale === "he" ? "חולון" : "Holon"}
                        />
                        <FormControlLabel
                          value="חיפה"
                          control={
                            <Radio
                              icon={CustomIcon}
                              checkedIcon={CustomCheckedIcon}
                            />
                          }
                          label={locale === "he" ? "חיפה" : "Haifa"}
                        />
                        <FormControlLabel
                          value="ירושלים"
                          control={
                            <Radio
                              icon={CustomIcon}
                              checkedIcon={CustomCheckedIcon}
                            />
                          }
                          label={locale === "he" ? "ירושלים" : "Jerusalem"}
                        />
                      </RadioGroup>
                      {errors.learningArea && (
                        <FormHelperText>
                          {errors.learningArea.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </StyledInputDivs>
              <StyledButton
                variant="primary"
                type="submit"
                $isDisabled={!isValid || isSubmitting}
                disabled={isSubmitting}
              >
                {t("send")}
              </StyledButton>
            </StyledWrapperForm>
          </FormContainer>
          {RECAPTCHA_KEY && (
            <ReCAPTCHA
              ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
              sitekey={RECAPTCHA_KEY}
              size="invisible"
              hl="iw"
              tabIndex={-1}
              badge="bottomright"
            />
          )}
        </>
      )}
    </>
  )
}

export default CareerForm
